/**
 * T3-4.4：图案裁剪 Studio 历史重试。
 * - 部分成功：失败项走 retryImageCropItem（有 bbox → crop-by-bbox，否则 Worker 重检+裁）
 * - 全失败 / 无成功项：force 重跑整 task（runImageCropTaskBatch + forceReprocess）
 */
import { taskAPI } from '@/utils/api';
import {
  buildAiStudioImageSuccessResult,
  completeStudioImageTaskUpdate,
  markAiStudioImageTaskFailed,
} from '@/pages/ipe/workbench/utils/aiStudioImageTaskRecord';
import { buildRetryTaskApiPatch } from '@/pages/ipe/workbench/utils/ipeTaskActions';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { sanitizeIpeUserMessage, toIpeUserErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import { getImg2ImgOutputs } from '@/pages/ipe/workbench/utils/img2imgTaskResult';
import { runImageCropTaskBatch } from './imageCropProcessTask';
import { retryImageCropItem } from './imageCropTaskApi';
import { persistImageCropStudioOutputs } from './imageCropStudioPersist';
import { loadCropImagesForTask } from '../utils/imageCropLoadResults';
import { mapCropRowToDetailRow } from '../utils/imageCropRowStatus';
import { collectCropOutputUrlsFromDetail } from '../utils/imageCropDetailPrints';
import { clearImageCropResultCache } from '../utils/imageCropResultCache';
import { clearImageCropTaskCancelled } from '../utils/imageCropTaskCancel';
import { IMAGE_CROP_NAV_ID } from '../constants/imageCropConfig';

function readExistingOutputs(result) {
  const r = result && typeof result === 'object' ? result : {};
  if (Array.isArray(r.outputs) && r.outputs.length > 0) {
    return r.outputs.map((row, index) => ({
      ...row,
      index: Number.isFinite(Number(row?.index)) ? Number(row.index) : index,
      sourceIndex: Number.isFinite(Number(row?.sourceIndex))
        ? Number(row.sourceIndex)
        : index,
    }));
  }
  return getImg2ImgOutputs(r);
}

function readReferenceUrlsFromTaskResult(result) {
  const r = result && typeof result === 'object' ? result : {};
  const fromUrls = Array.isArray(r.referenceUrls) ? r.referenceUrls.filter(Boolean) : [];
  if (fromUrls.length > 0) return fromUrls;
  const fromPreviews = Array.isArray(r.referencePreviews) ? r.referencePreviews.filter(Boolean) : [];
  return fromPreviews;
}

function isSuccessfulOutput(row) {
  if (!row || row.status === 'failed') return false;
  if (Array.isArray(row.urls) && row.urls.length > 0) return true;
  return Boolean(String(row.url || row.outputUrl || row.imageUrl || '').trim());
}

function resolveRetryTargets(existingOutputs, imageUrls, imageIds) {
  const failedIndices = existingOutputs
    .map((row, idx) => ({ row, idx: Number(row?.sourceIndex ?? row?.index ?? idx) }))
    .filter(({ row }) => !isSuccessfulOutput(row))
    .map(({ idx }) => idx);

  const hasSuccess = existingOutputs.some((row) => isSuccessfulOutput(row));
  const indices = hasSuccess && failedIndices.length > 0
    ? [...new Set(failedIndices)].sort((a, b) => a - b)
    : imageUrls.map((_, i) => i);

  return indices
    .filter((i) => i >= 0 && i < imageUrls.length)
    .map((i) => ({
      sourceIndex: i,
      id: imageIds[i] || `retry-${i}`,
      url: imageUrls[i],
    }));
}

/** @returns {'item-retry' | 'force-full'} */
export function resolveImageCropStudioRetryStrategy(existingOutputs, { force = false } = {}) {
  if (force) return 'force-full';
  const hasSuccess = existingOutputs.some((row) => isSuccessfulOutput(row));
  const failedIndices = existingOutputs
    .map((row, idx) => ({ row, idx: Number(row?.sourceIndex ?? row?.index ?? idx) }))
    .filter(({ row }) => !isSuccessfulOutput(row))
    .map(({ idx }) => idx);
  if (hasSuccess && failedIndices.length > 0) return 'item-retry';
  return 'force-full';
}

function mergeRetryOutputs(previousOutputs, retryOutputs) {
  const merged = new Map();
  previousOutputs.forEach((row) => {
    const idx = Number(row?.sourceIndex ?? row?.index);
    if (!Number.isFinite(idx)) return;
    if (isSuccessfulOutput(row)) merged.set(idx, row);
  });
  retryOutputs.forEach((row) => {
    const idx = Number(row?.sourceIndex ?? row?.index);
    if (!Number.isFinite(idx)) return;
    merged.set(idx, row);
  });
  return [...merged.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, row]) => row);
}

function collectSuccessfulUrls(outputs) {
  return outputs
    .filter((row) => isSuccessfulOutput(row))
    .flatMap((row) => {
      if (Array.isArray(row.urls) && row.urls.length > 0) return row.urls;
      const single = String(row.url || row.outputUrl || row.imageUrl || '').trim();
      return single ? [single] : [];
    })
    .map((url) => String(url || '').trim())
    .filter(Boolean);
}

function buildOutputFromRetryResult(result, target) {
  const idx = target.sourceIndex;
  if (result?.status === 'success' && result?.croppedImageUrl) {
    const url = String(result.croppedImageUrl).trim();
    return {
      url,
      outputUrl: url,
      imageUrl: url,
      status: 'success',
      index: idx,
      sourceIndex: idx,
      sourceImageId: target.id,
    };
  }
  return {
    status: 'failed',
    index: idx,
    sourceIndex: idx,
    sourceImageId: target.id,
    error: result?.error || '重试失败',
  };
}

function buildOutputsFromCropRows(items, rows) {
  const rowMap = new Map();
  (rows || []).forEach((row) => {
    if (row?.imageId != null) rowMap.set(String(row.imageId), row);
  });

  return items.map((item, index) => {
    const row = rowMap.get(String(item.id));
    if (!row) {
      return {
        status: 'failed',
        index,
        sourceIndex: index,
        sourceImageId: item.id,
        error: '未返回裁剪结果',
      };
    }
    const detail = mapCropRowToDetailRow(item.id, row, item.url || '');
    const urls = collectCropOutputUrlsFromDetail(detail);
    if (detail.status === 'success' && urls.length > 0) {
      return {
        url: urls[0],
        urls,
        outputUrl: urls[0],
        imageUrl: urls[0],
        status: 'success',
        index,
        sourceIndex: index,
        sourceImageId: item.id,
      };
    }
    return {
      status: 'failed',
      index,
      sourceIndex: index,
      sourceImageId: item.id,
      error: detail.error || '裁剪失败',
    };
  });
}

async function retryFailedCropItems({
  taskId,
  taskMeta,
  retryTargets,
  cropRowMap,
}) {
  const retryOutputs = [];
  for (const target of retryTargets) {
    const row = cropRowMap.get(String(target.id));
    const result = await retryImageCropItem({
      taskId,
      imageId: target.id,
      imageUrl: target.url,
      cropPosition: row?.cropPosition ?? null,
    });
    retryOutputs.push(buildOutputFromRetryResult(result, target));
  }
  return retryOutputs;
}

async function forceRetryFullCropTask({
  taskId,
  taskMeta,
  imageIds,
  imageUrls,
  sources,
}) {
  clearImageCropResultCache(taskId);

  const urlByImageId = new Map(
    imageIds.map((id, index) => [String(id), imageUrls[index] || '']),
  );

  await runImageCropTaskBatch({
    taskId,
    taskMeta,
    imageIds,
    resolveOriginalUrl: async (imageId) => urlByImageId.get(String(imageId)) || '',
    forceReprocess: true,
  });

  const rows = await loadCropImagesForTask(taskMeta, imageIds);
  return buildOutputsFromCropRows(sources, rows);
}

function buildInitialRetryResult(task, r, rawRefs, imageUrls, operatorName) {
  return {
    referencePreviews: Array.isArray(r.referencePreviews) ? r.referencePreviews : rawRefs,
    referenceUrls: imageUrls,
    previewUrl: rawRefs[0] || imageUrls[0] || '',
    operator: operatorName,
    operatorName,
    failureHint: '',
    engine: 'qwen-detect',
  };
}

async function finalizeStudioCropRetry({
  taskId,
  initialResult,
  mergedOutputs,
  estimatedTotal,
}) {
  const collectedUrls = collectSuccessfulUrls(mergedOutputs);
  if (collectedUrls.length === 0) {
    throw new Error('重试完成但未产出裁剪图');
  }

  const failedCount = mergedOutputs.filter((row) => row?.status === 'failed').length;
  const failureHint = mergedOutputs
    .filter((row) => row?.status === 'failed')
    .map((row) => sanitizeIpeUserMessage(row.error, '裁剪失败'))
    .filter(Boolean)
    .join('；');

  const successResult = buildAiStudioImageSuccessResult({
    ...initialResult,
    outputs: mergedOutputs,
    ...(failureHint ? { failureHint } : {}),
  }, {
    engine: 'qwen-detect',
    outputs: mergedOutputs,
    urls: collectedUrls,
  }, collectedUrls);

  const status = failedCount > 0 ? 'partial' : 'success';
  const finalResult = await completeStudioImageTaskUpdate({
    taskId,
    status,
    successCount: collectedUrls.length,
    failedCount,
    successResult,
    navId: IMAGE_CROP_NAV_ID,
  });

  return { finalResult, collectedUrls, failedCount, mergedOutputs };
}

/**
 * 图案裁剪历史重试。
 * @param {object} task
 * @param {{ force?: boolean }} [options] - force=true 时整 task 重跑
 */
export async function retryImageCropStudioTask(task, options = {}) {
  const taskId = String(task?.id || '').trim();
  if (!taskId) throw new Error('任务无效');

  clearImageCropTaskCancelled(taskId);

  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const rawRefs = readReferenceUrlsFromTaskResult(r);
  const operatorName = String(r.operatorName || r.operator || task?.operator || '').trim();
  const imageIds = Array.isArray(task.imageIds) ? task.imageIds : [];
  const existingOutputs = readExistingOutputs(r);

  const imageUrls = [];
  for (const raw of rawRefs) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) continue;
    const resolved = await resolveImg2ImgReferenceForApi(trimmed);
    if (resolved) imageUrls.push(resolved);
  }
  if (imageUrls.length === 0) {
    throw new Error('任务缺少原图，无法重试');
  }

  const retryTargets = resolveRetryTargets(existingOutputs, imageUrls, imageIds);
  if (retryTargets.length === 0) {
    throw new Error('没有需要重试的图片');
  }

  const strategy = resolveImageCropStudioRetryStrategy(existingOutputs, options);
  const estimatedTotal = Math.max(1, imageUrls.length);
  const initialResult = buildInitialRetryResult(task, r, rawRefs, imageUrls, operatorName);
  const taskMeta = {
    id: taskId,
    subAccount: task?.subAccount,
    taskId: task?.taskId ?? task?.id ?? taskId,
  };
  const sources = imageIds.map((id, index) => ({
    id: id || `retry-${index}`,
    url: rawRefs[index] || imageUrls[index] || '',
    sourceIndex: index,
  }));

  await taskAPI.update(taskId, {
    ...buildRetryTaskApiPatch({
      total: estimatedTotal,
      successCount: 0,
      failedCount: 0,
    }),
    imageIds,
    result: initialResult,
  });

  try {
    let mergedOutputs;

    if (strategy === 'item-retry') {
      const cropRows = await loadCropImagesForTask(taskMeta, imageIds);
      const cropRowMap = new Map(
        (cropRows || []).map((row) => [String(row.imageId), row]),
      );
      const retryOutputs = await retryFailedCropItems({
        taskId,
        taskMeta,
        retryTargets,
        cropRowMap,
      });
      mergedOutputs = mergeRetryOutputs(existingOutputs, retryOutputs);
    } else {
      mergedOutputs = await forceRetryFullCropTask({
        taskId,
        taskMeta,
        imageIds: imageIds.length > 0
          ? imageIds
          : sources.map((item) => item.id),
        imageUrls,
        sources,
      });
    }

    const { finalResult, collectedUrls } = await finalizeStudioCropRetry({
      taskId,
      initialResult,
      mergedOutputs,
      estimatedTotal,
    });

    await persistImageCropStudioOutputs({
      taskId,
      sources,
      imageUrls,
      outputs: finalResult.outputs || mergedOutputs,
    });

    return { taskId, urls: finalResult.urls || collectedUrls, strategy };
  } catch (error) {
    await markAiStudioImageTaskFailed(taskId, {
      ...initialResult,
      failureHint: toIpeUserErrorMessage(error, '重试失败'),
    }, estimatedTotal, error);
    throw error;
  }
}
